import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import debounce from 'lodash.debounce';
import aulaLive from '@ublend-npm/aula-live';
import { useDispatch, useSelector, useStore } from 'react-redux';
import refreshAccessToken from '@core/actions/accessToken/refreshAccessToken.action';
import { getSocket } from '@core/actions/socket';
import { currentClassRoomId } from '@core/selectors/classroom';
import { getInitialSpaceId } from '@core/selectors/space/space.selectors';
import {
  fetchHomeUpcomingAssignments,
  HomeUpcomingAssignment,
} from '@core/assignments/api/homeUpcomingAssignments';
import { HomeViewRole } from '@app/constants/home';

const FETCH_LIMIT = 50;
const LIVE_UPDATE_DEBOUNCE_MS = 2000;

const ASSIGNMENT_LIVE_EVENTS = [
  'AssessmentItem:Assignment:Saved',
  'AssessmentItem:Assignment:Deleted',
  'Handin:Assignment:Created',
  'Handin:Assignment:Updated',
  'Handin:Assignment:Deleted',
  'Assignment:Submitted',
  'Assignment:Graded',
  'Handin:Assignment:GradesReleased',
  'AssessmentItem:Assignment:GradesReleased',
] as const;

type WidgetErrorType = 'api' | 'offline';

type UseHomeUpcomingAssignmentsOptions = {
  fetchLimit?: number;
  /** Home educator/student toggle — passed to the API as `role`. */
  viewRole: HomeViewRole;
};

const getErrorType = (error: unknown): WidgetErrorType => {
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return 'offline';
  }

  const axiosError = error as { code?: string; message?: string };

  if (
    axiosError?.code === 'ERR_NETWORK' ||
    axiosError?.message === 'Network Error'
  ) {
    return 'offline';
  }

  return 'api';
};

const useHomeUpcomingAssignments = ({
  fetchLimit = FETCH_LIMIT,
  viewRole,
}: UseHomeUpcomingAssignmentsOptions) => {
  const dispatch = useDispatch<any>();
  const store = useStore<any>();

  const spaceId = useSelector((state: any) => getInitialSpaceId(state));
  const activeSpaceId = useSelector((state: any) => currentClassRoomId(state));
  const socketId = useSelector((state: any) => state.socket?.id);
  const classRoomIdsKey = useSelector((state: any) =>
    Object.keys(state.data?.classRooms || {})
      .sort()
      .join(',')
  );

  const spaceIdsToSubscribe = useMemo(() => {
    const ids = new Set<string>();

    if (classRoomIdsKey) {
      classRoomIdsKey.split(',').forEach((id) => {
        if (id) ids.add(id);
      });
    }

    if (spaceId) ids.add(spaceId);
    if (activeSpaceId) ids.add(activeSpaceId);

    return Array.from(ids);
  }, [activeSpaceId, classRoomIdsKey, spaceId]);

  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [errorType, setErrorType] = useState<WidgetErrorType | null>(null);
  const [allItems, setAllItems] = useState<HomeUpcomingAssignment[]>([]);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);

  const isLoadingRef = useRef(false);
  const isLoadingMoreRef = useRef(false);
  const pageRef = useRef(0);
  const totalRef = useRef(0);
  const itemsLengthRef = useRef(0);
  const viewRoleRef = useRef(viewRole);

  isLoadingRef.current = isLoading;
  pageRef.current = page;
  totalRef.current = total;
  itemsLengthRef.current = allItems.length;
  viewRoleRef.current = viewRole;

  const hasMore = allItems.length < total;

  const loadAssignments = useCallback(
    async ({ showLoading = false }: { showLoading?: boolean } = {}) => {
      if (!spaceId) {
        setAllItems([]);
        setPage(0);
        setTotal(0);
        setErrorType(null);
        setIsLoading(false);
        return;
      }

      if (!navigator.onLine) {
        if (showLoading) {
          setErrorType('offline');
          setIsLoading(false);
        }
        return;
      }

      const requestRole = viewRole;

      try {
        if (showLoading) {
          setIsLoading(true);
          setErrorType(null);
        }

        const accessToken = await refreshAccessToken(
          dispatch,
          store.getState
        )({ spaceId });

        const { data } = await fetchHomeUpcomingAssignments({
          accessToken,
          limit: fetchLimit,
          page: 0,
          includeSubmitted: true,
          role: requestRole,
        });

        // Drop stale responses if the educator/student toggle changed mid-flight.
        if (viewRoleRef.current !== requestRole) {
          return;
        }

        setAllItems(data.upcomingAssignments || []);
        setPage(0);
        setTotal(data.total ?? 0);
        setErrorType(null);
      } catch (e: unknown) {
        if (viewRoleRef.current !== requestRole) {
          return;
        }

        if (showLoading) {
          setAllItems([]);
          setPage(0);
          setTotal(0);
          setErrorType(getErrorType(e));
        }
      } finally {
        if (showLoading && viewRoleRef.current === requestRole) {
          setIsLoading(false);
        }
      }
    },
    [dispatch, fetchLimit, spaceId, store, viewRole]
  );

  const loadMore = useCallback(async () => {
    if (
      !spaceId ||
      isLoadingMoreRef.current ||
      isLoadingRef.current ||
      itemsLengthRef.current >= totalRef.current
    ) {
      return;
    }

    if (!navigator.onLine) {
      return;
    }

    const requestRole = viewRoleRef.current;

    isLoadingMoreRef.current = true;
    setIsLoadingMore(true);

    try {
      const accessToken = await refreshAccessToken(
        dispatch,
        store.getState
      )({ spaceId });

      const nextPage = pageRef.current + 1;
      const { data } = await fetchHomeUpcomingAssignments({
        accessToken,
        limit: fetchLimit,
        page: nextPage,
        includeSubmitted: true,
        role: requestRole,
      });

      if (viewRoleRef.current !== requestRole) {
        return;
      }

      const nextItems = data.upcomingAssignments || [];

      if (!nextItems.length) {
        setTotal(itemsLengthRef.current);
        return;
      }

      setAllItems((prev) => {
        const existingIds = new Set(prev.map((item) => item.id));
        const uniqueNextItems = nextItems.filter(
          (item) => !existingIds.has(item.id)
        );
        return [...prev, ...uniqueNextItems];
      });
      setPage(nextPage);
      setTotal(data.total ?? totalRef.current);
    } catch {
      // Keep already-loaded items; user can scroll again to retry.
    } finally {
      isLoadingMoreRef.current = false;
      setIsLoadingMore(false);
    }
  }, [dispatch, fetchLimit, spaceId, store]);

  const loadAssignmentsRef = useRef(loadAssignments);
  loadAssignmentsRef.current = loadAssignments;

  const refreshAssignments = useMemo(
    () =>
      debounce(
        () => loadAssignmentsRef.current({ showLoading: false }),
        LIVE_UPDATE_DEBOUNCE_MS,
        { leading: true, trailing: true }
      ),
    []
  );

  useEffect(() => {
    let isMounted = true;

    const run = async () => {
      if (!isMounted) return;
      await loadAssignments({ showLoading: true });
    };

    run();

    return () => {
      isMounted = false;
    };
  }, [loadAssignments]);

  useEffect(() => {
    return () => {
      refreshAssignments.cancel();
    };
  }, [refreshAssignments]);

  useEffect(() => {
    if (!socketId) return undefined;

    const socket = getSocket();
    if (!socket) return undefined;

    let isCancelled = false;

    const subscribeToAllUserSpaces = async () => {
      if (!spaceIdsToSubscribe.length) return;

      const tokens = await Promise.all(
        spaceIdsToSubscribe.map(async (id) => {
          const token = await refreshAccessToken(
            dispatch,
            store.getState
          )({ spaceId: id });
          return token;
        })
      );

      if (isCancelled) return;

      tokens
        .filter(Boolean)
        .forEach((token) => aulaLive.subscribeByAccessToken(token));
    };

    const attachListeners = () => {
      ASSIGNMENT_LIVE_EVENTS.forEach((event) => {
        socket.off(event, refreshAssignments);
        socket.on(event, refreshAssignments);
      });
    };

    const handleSocketConnect = () => {
      subscribeToAllUserSpaces();
    };

    attachListeners();
    subscribeToAllUserSpaces();
    socket.on('connect', handleSocketConnect);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshAssignments();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      isCancelled = true;
      socket.off('connect', handleSocketConnect);
      ASSIGNMENT_LIVE_EVENTS.forEach((event) => {
        socket.off(event, refreshAssignments);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [dispatch, refreshAssignments, socketId, spaceIdsToSubscribe, store]);

  useEffect(() => {
    const handleOnline = () => {
      if (errorType === 'offline') {
        loadAssignments({ showLoading: true });
      } else {
        refreshAssignments();
      }
    };

    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, [errorType, loadAssignments, refreshAssignments]);

  return {
    allItems,
    errorType,
    isLoading,
    isLoadingMore,
    hasMore,
    loadMore,
    retry: () => loadAssignments({ showLoading: true }),
  };
};

export default useHomeUpcomingAssignments;
